import { DataProvider, fetchUtils, HttpError } from 'ra-core';
import * as apiClient from '../common/apiClient';
import { getQuestionsAnalysis } from '../common/apiClient';


/**
 * Resolve the bearer token the same way fetchJson does.
 *
 * Uploads go through a bare fetch() rather than httpClient, because FormData
 * must set its own multipart Content-Type boundary. That meant they used to
 * send no Authorization header at all -- which is why the JWT check in
 * api/application/controllers/api/Upload.php had been commented out. The check
 * is on again as of 2026-09-13, so the token has to travel with the upload.
 */
const getAuthHeader = (): Record<string, string> => {
  let token = localStorage.getItem('jwt_access_token');
  if (!token) {
    token = localStorage.getItem('token');
  }
  if (!token) return {};
  return { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` };
};

/**
 * @param field the multipart field name the API reads -- always 'file'
 * @param data  react-admin's file wrapper, carrying .rawFile
 * @param type  'image' for question images, 'content' for study PDFs. Decides
 *              which directory the API writes to; unknown values fall back to
 *              'image', which is where every caller before this wrote.
 */
const UploadFile = (field: string, data: any, type: string = 'image') => {
  const formData = new FormData();
  formData.append(field, data.rawFile);
  formData.append('type', type);
  let finalUrl = `${apiClient.APIUrl}/upload`;
  return fetch(`${finalUrl}`, {
    method: 'POST',
    body: formData,
    headers: getAuthHeader()
  })
    .then((response) => response.json())
    .then((responseJson) => {
      return responseJson;
    })
    .catch((error) => {
      console.log(error);
    });
};

/**
 * Upload one file field on an academic-content record, if a new file was picked.
 *
 * Shared by create and update because the handling is identical and the two
 * used to drift: `file_url` was wired into create only, so replacing a PDF on
 * an existing record silently saved the react-admin file object instead of a
 * URL.
 *
 * Leaves an untouched field alone -- on edit it arrives as the existing URL
 * string, with no rawFile, and must survive unchanged.
 *
 * Throws on a failed upload rather than saving an empty URL. The other fields
 * here blank out on failure, which for this resource would publish a row whose
 * link is broken; better that the admin sees the error and retries.
 *
 * @param field       the record field holding react-admin's file wrapper
 * @param destination the API's upload `type` -- which directory it lands in
 * @param recordSize  whether the uploaded byte count becomes file_size_bytes.
 *                    True for the material itself, FALSE for the thumbnail:
 *                    that column is shown to students as the size of what they
 *                    are about to open, so a thumbnail must never overwrite it.
 */
const attachUpload = async (
  data: any,
  field: string,
  destination: string,
  recordSize: boolean,
  label: string
) => {
  if (!data || !data.hasOwnProperty(field)) return;
  if (data[field] == null || data[field].rawFile == null) return;

  const response: any = await UploadFile('file', data[field], destination);
  if (!response || response.status !== 'success') {
    throw new Error(
      (response && response.message) || `The ${label} could not be uploaded. Nothing was saved.`
    );
  }
  data[field] = response.result;

  // The API returns the byte count so we do not have to stat the file again.
  // Students see it before opening a file on mobile data.
  if (recordSize && typeof response.size === 'number') {
    data.file_size_bytes = response.size;
  }
};

/**
 * Both file fields on an academic-content record.
 *
 * The material -- a PDF or, since migration 0011, an image -- and the card
 * thumbnail, which is a preview and never the material itself. They go to
 * different directories and only the first one sets file_size_bytes.
 *
 * Sequential rather than Promise.all on purpose: if the material fails, the
 * whole save is abandoned, and there is no reason to have put a stray
 * thumbnail on disk first.
 */
const attachContentFile = async (data: any) => {
  await attachUpload(data, 'file_url', 'content', true, 'file');
  await attachUpload(data, 'thumbnail_url', 'thumbnail', false, 'thumbnail');
};

const fetchJson = (url, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  
  // First check for JWT access token, fallback to old token
  let token = localStorage.getItem('jwt_access_token');
  if (!token) {
    token = localStorage.getItem('token');
  }
  
  if (token) {
    const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    options.headers.set('Authorization', authHeader);
  }
  
  return fetchUtils.fetchJson(url, options);
};

export const MyDataProvider = (
  apiUrl: string,
  httpClient = fetchJson
): DataProvider => {
  return {
    getList: (resource: any, params: any) => {
      // Handle special case for notification-preview
      if (resource === 'notification-preview') {
        const { page, perPage } = params.pagination;
        const filter = params.filter || {};
        
        const queryParams = new URLSearchParams({
          limit: perPage.toString(),
          page: page.toString()
        });
        
        if (filter.search) {
          queryParams.append('search', filter.search);
        }
        if (filter.notification_type) {
          queryParams.append('notification_type', filter.notification_type);
        }
        if (filter.priority) {
          queryParams.append('priority', filter.priority);
        }
        if (filter.segment) {
          queryParams.append('segment', filter.segment);
        }
        
        const finalUrl = `${apiUrl}/notification_preview?${queryParams}`;
        return httpClient(finalUrl).then(({ headers, json }) => {
          return {
            data: json.data || [],
            total: json.pagination?.total_eligible_users || json.data?.length || 0
          };
        });
      }

      // Handle special case for question-analysis
      if (resource === 'question-analysis') {
        return getQuestionsAnalysis(params).then((result) => {
          return {
            data: result.data,
            total: result.total
          };
        });
      }

      // Handle special case for flagged-questions
      if (resource === 'flagged-questions') {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const filter = params.filter;
        const filterString = JSON.stringify(filter);
        const finalUrl = `${apiUrl}/question/flagged?pagesize=${perPage}&page=${page}&sortby=${field}&sortorder=${order}&filter=${filterString}`;
        return httpClient(finalUrl).then(({ headers, json }) => {
          return {
            data: json.result || [],
            total: json.pagination ? json.pagination.total_records : (json.result ? json.result.length : 0)
          };
        });
      }

      // Handle special case for quiz resource
      if (resource === 'quiz') {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const filter = params.filter;
        const filterString = JSON.stringify(filter);
        const finalUrl = `${apiUrl}/quiz?pagesize=${perPage}&page=${page}&sortby=${field}&sortorder=${order}&filter=${filterString}`;
        return httpClient(finalUrl).then(({ headers, json }) => {
          return {
            data: json.result || [],
            total: json.total || 0
          };
        });
      }

      if (resource === 'user') {
        let filterInner =   params.filter;
        if(filterInner.hasOwnProperty('q')){
          filterInner.display_name = filterInner.q;
          delete filterInner.q;
          params.filter = filterInner;
        }
      }
      
      // Default behavior for other resources
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const filter = params.filter;
      const filterString = JSON.stringify(filter);
      const finalUrl = `${apiUrl}/${resource}?pagesize=${perPage}&page=${page}&sortby=${field}&sortorder=${order}&filter=${filterString}`;
      return httpClient(finalUrl).then(({ headers, json }) => {
        return {
          data: json.result,
          total: json.total
        };
      });
    },
    getOne: (resource: any, params: any) => {
      const finalUrl = `${apiUrl}/${resource}/${params.id}`;
      return httpClient(finalUrl).then(({ headers, json }) => {
        return {
          data: json.result,
          total: json.total
        };
      });
    },
    getMany: (resource: any, params: any) => {
      var ids = params.ids;
      let where = '';
      if (ids.length == 1) {
        where = `mid=` + ids[0];
      } else {
        where = `mid=`;
        ids.forEach(function (id, index) {
          where += `${id},`;
        });
      }

      const finalUrl = `${apiUrl}/${resource}?${where}`;
      return httpClient(finalUrl).then(({ headers, json }) => {
        return {
          data: json.result,
          total: json.total
        };
      });
    },
    getManyReference: (resource: any, params: any) => {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const filter = params.filter;
      

      if (params.target != undefined) {
        let col = params.target + '=';
        let val = params.id;
        filter[col] = val;
      }

      const filterString = JSON.stringify(filter);
      //debugger;

      // var ids = params.ids;
      // let where = "";
      // if (ids) {
      //     if (ids.length == 1) {
      //         where = `&mid=` + ids[0];
      //     } else {
      //         where = `&mid=`;
      //         ids.forEach(function (id, index) {
      //             where += `${id},`;
      //         });
      //     }
      // }
      //const finalUrl = `${apiUrl}/${resource}?pagesize=${perPage}&page=${page}&sortby=${field}&sortorder=${order}&filter=${filterString}${where}`;
      const finalUrl = `${apiUrl}/${resource}?pagesize=${perPage}&page=${page}&sortby=${field}&sortorder=${order}&filter=${filterString}`;

      return httpClient(finalUrl).then(({ headers, json }) => {
        return {
          data: json.result,
          total: json.total
        };
      });
    },
    create: async (resource: any, params: any) => {
      let data = params.data;
      

      if (data.hasOwnProperty("quiz_detail_image")) {
        if (data.quiz_detail_image != null && data.quiz_detail_image.rawFile != null) {
          let response = await UploadFile("file", data.quiz_detail_image);
          if (response.status === 'success') {
            data.quiz_detail_image = response.result;
          } else {
            data.quiz_detail_image = '';
          }
        }
      }

      if (data.hasOwnProperty('cover_image')) {
        if (data.cover_image != null && data.cover_image.rawFile != null) {
          let response = await UploadFile('file', data.cover_image);
          if (response.status === 'success') {
            data.cover_image = response.result;
          } else {
            data.cover_image = '';
          }
        }
      }

      if (data.hasOwnProperty('question_img_url')) {
        if (
          data.question_img_url != null &&
          data.question_img_url.rawFile != null
        ) {
          let response = await UploadFile('file', data.question_img_url);
          if (response.status === 'success') {
            data.question_img_url = response.result;
          } else {
            data.question_img_url = '';
          }
        }
      }

      await attachContentFile(data);

      const finalUrl = `${apiUrl}/${resource}`;
      return httpClient(finalUrl, {
        method: 'POST',
        body: JSON.stringify(data)
      }).then(({ headers, json }) => {
        return {
          data: json.result,
          total: json.total
        };
      });
    },
    update: async (resource: any, params: any) => {
      let data = params.data;

      if (data.hasOwnProperty('question_img_url')) {
        if (
          data.question_img_url != null &&
          data.question_img_url.rawFile != null
        ) {
          let response = await UploadFile('file', data.question_img_url);
          if (response.status === 'success') {
            data.question_img_url = response.result;
          } else {
            data.question_img_url = '';
          }
        }
      }

      if (data.hasOwnProperty("quiz_detail_image")) {
        if (data.quiz_detail_image != null && data.quiz_detail_image.rawFile != null) {
          let response = await UploadFile("file", data.quiz_detail_image);
          if (response.status === 'success') {
            data.quiz_detail_image = response.result;
          } else {
            data.quiz_detail_image = '';
          }
        }
      }

      if (data.hasOwnProperty('cover_image')) {
        if (data.cover_image != null && data.cover_image.rawFile != null) {
          let response = await UploadFile('file', data.cover_image);
          if (response.status === 'success') {
            data.cover_image = response.result;
          } else {
            data.cover_image = '';
          }
        }
      }

      await attachContentFile(data);

      const finalUrl = `${apiUrl}/${resource}/${params.id}`;
      return httpClient(finalUrl, {
        method: 'PUT',
        body: JSON.stringify(data)
      }).then(({ headers, json }) => {
        return {
          data: json.result,
          total: json.total
        };
      });
    },
    updateMany: (resource: any, params: any) => {
      const finalUrl = `${apiUrl}/${resource}`;
      return httpClient(finalUrl).then(({ headers, json }) => {
        return {
          data: json.result,
          total: 10
        };
      });
    },
    delete: (resource: any, params: any) => {
      const finalUrl = `${apiUrl}/${resource}/${params.id}`;
      return httpClient(finalUrl, {
        method: 'DELETE'
      }).then(({ headers, json }) => {
        return {
          data: json.result,
          total: json.total
        };
      });
    },
    deleteMany: (resource: any, params: any) => {
      const finalUrl = `${apiUrl}/${resource}`;
      return httpClient(finalUrl, {
        method: 'DELETE'
      }).then(({ headers, json }) => {
        return {
          data: json.result,
          total: 10
        };
      });
    }
  };
};
